import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Tabs, 
  Table, 
  Button, 
  DatePicker, 
  Space, 
  message, 
  Typography, 
  Spin, 
  Statistic, 
  Row, 
  Col 
} from 'antd';
import { DownloadOutlined, FileExcelOutlined, ReloadOutlined } from '@ant-design/icons';
import axios from 'axios';
import ExcelJS from 'exceljs';
import dayjs from 'dayjs';
import constants from '../../constants';

const { RangePicker } = DatePicker;
const { Title } = Typography;
const { TabPane } = Tabs;

const MonthlyReportComponent = () => {
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState({
    paid: [],
    training: [],
    interview: [],
    internship: [],
    noInterview: [],
    noInternship: []
  });
  const [dateRange, setDateRange] = useState([
    dayjs().startOf('month'),
    dayjs().endOf('month')
  ]);

  // Fetch report data
  const fetchReportData = async () => {
    setLoading(true);
    try {
      const startDate = dateRange[0]?.format('YYYY-MM-DD');
      const endDate = dateRange[1]?.format('YYYY-MM-DD');
      let url
      if(startDate && endDate){
      url=`${constants.NEW_DOMAIN}/api/v1/admin/generateMonthlyReport?startDate=${startDate}&endDate=${endDate}`
      }else{
      url=`${constants.NEW_DOMAIN}/api/v1/admin/generateMonthlyReport`
      }
     
      const res = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${process.env.REACT_APP_JWT_TOKEN}`
        }
      })
      
      setReportData(res.data.data || {
        paid: [],
        training: [],
        interview: [],
        internship: [],
        noInterview: [],
        noInternship: []
      });
      message.success('Report data loaded successfully');
    } catch (error) {
      console.error('Failed to fetch report:', error);
      message.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData();
  }, []);

  // Column definitions for each tab
  const paidColumns = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Phone', dataIndex: 'phone', key: 'phone' },
    { title: 'Category', dataIndex: 'category', key: 'category' },
    // { title: 'DOB', dataIndex: 'dob', key: 'dob', render: (date) => date ? dayjs(date).format('DD/MM/YYYY') : '-' },
    // { title: 'Standard', dataIndex: 'standard', key: 'standard' },
    // { title: 'Program', dataIndex: 'program', key: 'program' }
  ];

  const trainingColumns = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Phone', dataIndex: 'phone', key: 'phone' },
    { title: 'Category', dataIndex: 'category', key: 'category' },
    { title: 'Batch', dataIndex: 'batch', key: 'batch' },
    { 
      title: 'Completion', 
      dataIndex: 'completion', 
      key: 'completion',
      render: (value) => `${value || 0}%`
    }
  ];

  const interviewColumns = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Phone', dataIndex: 'phone', key: 'phone' },
    { title: 'Category', dataIndex: 'category', key: 'category' },
    { title: 'Score', dataIndex: 'score', key: 'score' },
    { title: 'Feedback', dataIndex: 'feedback', key: 'feedback', ellipsis: true },
    { title: 'Improvement', dataIndex: 'improvement', key: 'improvement', ellipsis: true }
  ];

  const internshipColumns = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Phone', dataIndex: 'phone', key: 'phone' },
    { title: 'Program', dataIndex: 'program', key: 'program' },
    { title: 'Company', dataIndex: 'company', key: 'company' },
    { title: 'Start Date', dataIndex: 'startDate', key: 'startDate' },
    { 
      title: 'Week 1', 
      dataIndex: 'task1', 
      key: 'task1',
      render: (url) => url ? <a href={url} target="_blank" rel="noopener noreferrer">View</a> : 'Not Submitted'
    },
    { 
      title: 'Week 2', 
      dataIndex: 'task2', 
      key: 'task2',
      render: (url) => url ? <a href={url} target="_blank" rel="noopener noreferrer">View</a> : 'Not Submitted'
    },
    { 
      title: 'Week 3', 
      dataIndex: 'task3', 
      key: 'task3',
      render: (url) => url ? <a href={url} target="_blank" rel="noopener noreferrer">View</a> : 'Not Submitted'
    },
    { 
      title: 'Week 4', 
      dataIndex: 'task4', 
      key: 'task4',
      render: (url) => url ? <a href={url} target="_blank" rel="noopener noreferrer">View</a> : 'Not Submitted'
    }
  ];

  const noInterviewColumns = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Phone', dataIndex: 'phone', key: 'phone' },
    { title: 'Category', dataIndex: 'category', key: 'category' },
    { title: 'Batch', dataIndex: 'batch', key: 'batch' },
    { 
      title: 'Interview Status', 
      dataIndex: 'interviewStatus', 
      key: 'interviewStatus',
      render: (status) => {
        let color;
        switch(status) {
          case 'SCHEDULED': color = 'blue'; break;
          case 'PENDING': color = 'orange'; break;
          case 'REJECTED': color = 'red'; break;
          default: color = 'gray';
        }
        return <span style={{ color }}>{status || 'NOT_STARTED'}</span>;
      }
    }
  ];

  const noInternshipColumns = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Phone', dataIndex: 'phone', key: 'phone' },
    { title: 'Program', dataIndex: 'program', key: 'program' },
    { title: 'Interview Score', dataIndex: 'interviewScore', key: 'interviewScore' },
    { title: 'Company Assigned', dataIndex: 'companyAssigned', key: 'companyAssigned' },
    { 
      title: 'Offer Letter Status', 
      dataIndex: 'offerLetterStatus', 
      key: 'offerLetterStatus',
      render: (status) => {
        let color;
        switch(status) {
          case 'Accepted': color = 'green'; break;
          case 'Generated but not accepted': color = 'orange'; break;
          default: color = 'red';
        }
        return <span style={{ color }}>{status}</span>;
      }
    }
  ];

  // Function to download Excel report
  const downloadExcelReport = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      
      // Add worksheets for each data category
      const addWorksheet = (name, data, columns) => {
        const sheet = workbook.addWorksheet(name);
        
        // Add header row
        const headerRow = columns.map(col => col.title);
        sheet.addRow(headerRow);
        
        // Format header row
        sheet.getRow(1).font = { bold: true };
        sheet.getRow(1).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFD3D3D3' }
        };
        
        // Add data rows
        data.forEach(item => {
          const rowData = columns.map(col => {
            const value = item[col.dataIndex];
            // Handle URL rendering for tasks
            if (col.dataIndex.startsWith('task') && value) {
              return 'Submitted';
            }
            return value !== undefined ? value : '';
          });
          sheet.addRow(rowData);
        });
        
        // Auto-fit columns
        columns.forEach((col, i) => {
          let maxLength = col.title.length;
          data.forEach(item => {
            const value = item[col.dataIndex];
            if (value) {
              const valueLength = String(value).length;
              if (valueLength > maxLength) {
                maxLength = valueLength;
              }
            }
          });
          sheet.getColumn(i + 1).width = Math.min(maxLength + 2, 30);
        });
      };

      // Add worksheets for each category
      addWorksheet('Overall Users', reportData.paid, paidColumns);
      addWorksheet('Training Users', reportData.training, trainingColumns);
      addWorksheet('Interview Completed', reportData.interview, interviewColumns);
      addWorksheet('Internship Started', reportData.internship, internshipColumns);
      addWorksheet('Pending Interviews', reportData.noInterview, noInterviewColumns);
      addWorksheet('Pending Internships', reportData.noInternship, noInternshipColumns);
      
      // Generate file name with date range
      const startDate = dateRange[0]?.format('YYYY-MM-DD') || 'all';
      const endDate = dateRange[1]?.format('YYYY-MM-DD') || 'all';
      const fileName = `Monthly_Report_${startDate}_to_${endDate}.xlsx`;
      
      // Write to buffer and download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      window.URL.revokeObjectURL(url);
      
      message.success('Report downloaded successfully');
    } catch (error) {
      console.error('Failed to download report:', error);
      message.error('Failed to download report');
    }
  };

  // Statistics summary
  const renderStatistics = () => (
    <Row gutter={16} style={{ marginBottom: '24px' }}>
      <Col span={4}>
        <Card>
          <Statistic 
            title="Total Users" 
            value={reportData.paid.length} 
            prefix={<FileExcelOutlined />} 
          />
        </Card>
      </Col>
      <Col span={4}>
        <Card>
          <Statistic 
            title="In Training" 
            value={reportData.training.length} 
            valueStyle={{ color: '#1890ff' }}
          />
        </Card>
      </Col>
      <Col span={4}>
        <Card>
          <Statistic 
            title="Interviews Completed" 
            value={reportData.interview.length} 
            valueStyle={{ color: '#52c41a' }}
          />
        </Card>
      </Col>
      <Col span={4}>
        <Card>
          <Statistic 
            title="Internships Started" 
            value={reportData.internship.length} 
            valueStyle={{ color: '#722ed1' }}
          />
        </Card>
      </Col>
      <Col span={4}>
        <Card>
          <Statistic 
            title="Pending Interviews" 
            value={reportData.noInterview.length} 
            valueStyle={{ color: '#fa8c16' }}
          />
        </Card>
      </Col>
      <Col span={4}>
        <Card>
          <Statistic 
            title="Pending Internships" 
            value={reportData.noInternship.length} 
            valueStyle={{ color: '#eb2f96' }}
          />
        </Card>
      </Col>
    </Row>
  );
  
  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <Space direction="vertical" style={{ width: '100%' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
            <Title level={3}>Monthly Progress Report</Title>
            <Space>
              <RangePicker
                value={dateRange}
                onChange={(dates) => setDateRange(dates)}
                format="YYYY-MM-DD"
              />
              <Button 
                type="primary" 
                icon={<ReloadOutlined />} 
                onClick={fetchReportData}
              >
                Refresh
              </Button>
              <Button 
                type="primary" 
                icon={<DownloadOutlined />} 
                onClick={downloadExcelReport}
              >
                Download Excel
              </Button>
            </Space>
          </div>
          
          {loading ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <Spin size="large" />
            </div>
          ) : (
            <>
              {renderStatistics()}
              
              <Tabs defaultActiveKey="paid">
                <TabPane tab="Overall Users" key="paid">
                  <Table 
                    dataSource={reportData.paid} 
                    columns={paidColumns} 
                    rowKey="email"
                    pagination={{ pageSize: 10 }}
                    scroll={{ x: 'max-content' }}
                  />
                </TabPane>
                <TabPane tab="Training" key="training">
                  <Table 
                    dataSource={reportData.training} 
                    columns={trainingColumns} 
                    rowKey="email"
                    pagination={{ pageSize: 10 }}
                    scroll={{ x: 'max-content' }}
                  />
                </TabPane>
                <TabPane tab="Interviews Completed" key="interview">
                  <Table 
                    dataSource={reportData.interview} 
                    columns={interviewColumns} 
                    rowKey="email"
                    pagination={{ pageSize: 10 }}
                    scroll={{ x: 'max-content' }}
                  />
                </TabPane>
                <TabPane tab="Internships Started" key="internship">
                  <Table 
                    dataSource={reportData.internship} 
                    columns={internshipColumns}
                    rowKey="email" 
                    pagination={{ pageSize: 10 }}
                    scroll={{ x: 'max-content' }}
                  />
                </TabPane>
                <TabPane tab="Pending Interviews" key="noInterview">
                  <Table 
                    dataSource={reportData.noInterview} 
                    columns={noInterviewColumns} 
                    rowKey="email"
                    pagination={{ pageSize: 10 }}
                    scroll={{ x: 'max-content' }}
                  />
                </TabPane>
                <TabPane tab="Pending Internships" key="noInternship">
                  <Table 
                    dataSource={reportData.noInternship} 
                    columns={noInternshipColumns} 
                    rowKey="email"
                    pagination={{ pageSize: 10 }}
                    scroll={{ x: 'max-content' }}
                  />
                </TabPane>
              </Tabs>
            </>
          )}
        </Space>
      </Card>
    </div>
  );
};

export default MonthlyReportComponent;